(() => {
  if (customElements.get('aurvex-try-on')) return;
  const PLAYBACK_GAIN = 0.25;
  class AurvexTryOn extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.dialog = this.querySelector('dialog');
      this.copy = JSON.parse(this.querySelector('[data-copy]').textContent);
      this.quiet = false;
      this.playing = false;
      this.generation = 0;
      const options = { signal: this.controller.signal };
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-action]');
        if (!button) return;
        const action = button.dataset.action;
        if (action === 'open') this.open();
        if (action === 'close') this.dialog.close();
        if (action === 'start') this.start();
        if (action === 'switch' && this.context) {
          this.quiet = !this.quiet; this.updateSound(); this.render();
          if (this.context.state !== 'running') this.context.resume().catch(() => this.resetIntro());
        }
      }, options);
      this.dialog.addEventListener('close', () => { this.stop(); this.opener?.focus({ preventScroll: true }); }, options);
      this.dialog.addEventListener('click', (event) => { if (event.target === this.dialog) this.dialog.close(); }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause();
        else if (this.context && this.dialog.open) this.context.resume().catch(() => this.resetIntro());
      }, options);
      if (this.hasAttribute('data-card-player')) document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-axtry-open]');
        if (!trigger) return;
        event.preventDefault(); event.stopImmediatePropagation();
        this.dataset.tone = trigger.dataset.tone;
        const image = this.querySelector('[data-product-image]');
        image.src = trigger.dataset.image; image.alt = trigger.dataset.title; image.hidden = false;
        this.opener = trigger;
        this.open();
      }, { capture: true, signal: this.controller.signal });
      window.addEventListener('pagehide', () => this.stop(), options);
      document.addEventListener('aurvex:try-on-open', (event) => { if (event.detail !== this && this.dialog.open) this.dialog.close(); }, options);
      this.render();
    }
    disconnectedCallback() {
      this.stop();
      this.controller?.abort();
      this.controller = null;
      if (this.dialog?.open) this.dialog.close();
    }
    open() {
      document.dispatchEvent(new CustomEvent('aurvex:try-on-open', { detail: this }));
      this.querySelector('[data-intro]').hidden = false;
      this.querySelector('[data-experience]').hidden = true;
      this.querySelector('[data-error]').textContent = '';
      this.dialog.showModal();
      this.dialog.scrollTop = 0;
      this.querySelector('.axtry__sheet').scrollTop = 0;
    }
    async start() {
      if (this.busy || this.context) return;
      const Audio = window.AudioContext || window.webkitAudioContext;
      if (!Audio) { this.querySelector('[data-error]').textContent = this.copy.error; return; }
      const generation = ++this.generation;
      const startButton = this.querySelector('[data-action="start"]');
      this.busy = true;
      startButton.disabled = true;
      try {
        // Safari otherwise treats Web Audio as ambient sound and may mute it
        // with the iPhone silent switch, even while the context is running.
        if (navigator.audioSession) {
          this.previousAudioSession = navigator.audioSession.type;
          navigator.audioSession.type = 'playback';
        }
        const context = new Audio();
        this.context = context;
        await context.resume();
        if (generation !== this.generation || !this.dialog.open || document.hidden) { this.stop(); return; }
        if (context.state !== 'running') throw new Error('Audio unavailable');
        if (!this.audioBytes) {
          const response = await fetch(this.dataset.audioUrl, { signal: this.controller.signal });
          if (!response.ok) throw new Error('Audio load failed');
          this.audioBytes = await response.arrayBuffer();
        }
        const buffer = await context.decodeAudioData(this.audioBytes.slice(0));
        if (generation !== this.generation) return;
        if (!this.dialog.open || document.hidden) { this.stop(); return; }
        if (context.state !== 'running') await context.resume();
        if (generation !== this.generation) return;
        if (context.state !== 'running') throw new Error('Audio interrupted');
        this.buildSound(context, buffer);
        this.quiet = false;
        this.playing = true;
        this.querySelector('[data-intro]').hidden = true;
        this.querySelector('[data-experience]').hidden = false;
        this.dialog.scrollTop = 0;
        context.addEventListener('statechange', () => {
          if (this.context !== context) return;
          this.playing = context.state === 'running';
          this.render();
        }, { signal: this.controller.signal });
        this.updateSound(true);
        this.render();
        this.animateWave();
        this.querySelector('[data-action="switch"]').focus({ preventScroll: true });
      } catch (error) {
        this.stop();
        this.querySelector('[data-error]').textContent = this.copy.error;
      } finally {
        this.busy = false;
        startButton.disabled = false;
      }
    }
    buildSound(context, buffer) {
      this.source = context.createBufferSource();
      this.source.buffer = buffer;
      this.source.loop = true;
      this.dry = context.createGain();
      this.wet = context.createGain();
      this.filter = context.createBiquadFilter();
      this.filter.type = 'lowpass';
      this.filter.frequency.value = 1300;
      this.filter.Q.value = 0.5;
      this.master = context.createGain();
      this.master.gain.value = 0;
      this.dry.gain.value = 1;
      this.wet.gain.value = 0;
      this.source.connect(this.dry).connect(this.master);
      this.source.connect(this.filter).connect(this.wet).connect(this.master);
      this.analyser = context.createAnalyser();
      this.analyser.fftSize = 2048;
      this.analyser.smoothingTimeConstant = 0.85;
      this.waveData = new Uint8Array(this.analyser.frequencyBinCount);
      this.limiter = context.createDynamicsCompressor();
      this.limiter.threshold.value = -20;
      this.limiter.knee.value = 6;
      this.limiter.ratio.value = 8;
      this.limiter.attack.value = 0.005;
      this.limiter.release.value = 0.2;
      this.master.connect(this.limiter).connect(this.analyser).connect(context.destination);
      this.source.start();
    }
    updateSound(starting = false) {
      if (!this.context || !this.master) return;
      const t = this.context.currentTime;
      const ramp = (parameter, value) => {
        parameter.cancelScheduledValues(t);
        parameter.setTargetAtTime(value, t, 0.06);
      };
      ramp(this.dry.gain, this.quiet ? 0 : 1);
      ramp(this.wet.gain, this.quiet ? 0.34 : 0);
      // Fixed moderate level across every colour and entry point.
      const level = PLAYBACK_GAIN;
      if (starting) this.master.gain.setTargetAtTime(level, t, 0.4);
      else ramp(this.master.gain, level);
    }
    resetIntro() {
      this.stop();
      this.querySelector('[data-intro]').hidden = false;
      this.querySelector('[data-experience]').hidden = true;
    }
    animateWave() {
      cancelAnimationFrame(this.frame);
      const path = this.querySelector('[data-wave]');
      const trail = this.querySelector('[data-wave-trail]');
      const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      const backgroundAnimations = this.querySelector('.axtry__sheet')
        .getAnimations({ subtree: true })
        .filter(animation => ['axtry-orbit', 'axtry-halo', 'axtry-satin', 'axtry-light'].includes(animation.animationName));
      let motionSpeed = 1;
      let amplitude = 0;
      let previous = 0;
      const draw = (now) => {
        if (!this.context || !this.dialog.open) return;
        this.frame = requestAnimationFrame(draw);
        if (now - previous < 32) return;
        const elapsed = previous ? Math.min((now - previous) / 1000, 0.1) : 0;
        previous = now;
        const reduced = motionPreference.matches;
        const targetSpeed = reduced || !this.playing ? 0 : this.quiet ? 0.025 : 1;
        // Change playback rate, not duration, so a tap never jumps the rings' phase.
        motionSpeed += (targetSpeed - motionSpeed) * (1 - Math.exp(-elapsed / 0.7));
        for (const animation of backgroundAnimations) animation.updatePlaybackRate(motionSpeed);
        this.analyser.getByteFrequencyData(this.waveData);
        let energy = 0;
        for (let i = 1; i < 180; i++) energy += this.waveData[i] / 255;
        const target = this.playing ? Math.min(1, energy / 179) : 0;
        amplitude += (target - amplitude) * 0.12;
        const phase = reduced ? 0 : now * 0.0014;
        const points = [];
        for (let i = 0; i <= 100; i++) {
          const x = i * 4;
          const envelope = Math.pow(Math.sin(Math.PI * i / 100), 1.4);
          const wave = Math.sin(i * 0.19 - phase) * 0.65 + Math.sin(i * 0.11 + phase * 0.7) * 0.35;
          points.push(`${i ? 'L' : 'M'}${x} ${(50 + wave * envelope * amplitude * 56).toFixed(2)}`);
        }
        path.setAttribute('d', points.join(' '));
        trail.setAttribute('d', points.join(' '));
      };
      this.frame = requestAnimationFrame(draw);
    }
    pause() {
      if (this.context?.state === 'running') {
        this.context.suspend().catch(() => {});
        this.playing = false;
        this.render();
      }
    }
    stop() {
      ++this.generation;
      cancelAnimationFrame(this.frame);
      const context = this.context;
      this.context = null;
      try { this.source?.stop(); } catch (error) {}
      this.source = null;
      if (context && context.state !== 'closed') context.close().catch(() => {});
      if (this.previousAudioSession !== undefined) {
        try { navigator.audioSession.type = this.previousAudioSession; } catch (error) {}
        this.previousAudioSession = undefined;
      }
      this.playing = false;
      this.quiet = false;
      this.render();
    }
    render() {
      if (!this.dialog) return;
      this.dialog.dataset.quiet = String(this.quiet);
      this.dialog.dataset.playing = String(this.playing);
      const toggle = this.querySelector('[data-action="switch"]');
      toggle.setAttribute('aria-pressed', String(this.quiet));
      this.querySelector('[data-switch-label]').textContent = this.quiet ? this.copy.switchOff : this.copy.switchOn;
      this.querySelector('[data-state-title]').textContent = this.quiet ? 'QUIET ON.' : 'WORLD ON.';

    }
  }
  customElements.define('aurvex-try-on', AurvexTryOn);
})();
